import type { AxiosRequestConfig } from "axios";

import apiClient from "./client";

type CacheEntry = {
  value?: unknown;
  promise?: Promise<unknown>;
};

const getCache = new Map<string, CacheEntry>();

const stableSerialize = (value: unknown): string => {
  if (value === undefined) return "undefined";
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableSerialize).join(",")}]`;

  return `{${Object.entries(value as Record<string, unknown>)
    .filter(([, item]) => item !== undefined)
    .sort(([first], [second]) => first.localeCompare(second))
    .map(([key, item]) => `${JSON.stringify(key)}:${stableSerialize(item)}`)
    .join(",")}}`;
};

const createKey = (url: string, config?: AxiosRequestConfig): string =>
  `${url}?${stableSerialize(config?.params ?? {})}`;

/**
 * Centralise tous les GET publics : une même requête n'est exécutée qu'une fois,
 * y compris lorsque plusieurs composants la lancent simultanément.
 */
export const cachedGet = async <T>(
  url: string,
  config?: AxiosRequestConfig,
  force = false
): Promise<T> => {
  const key = createKey(url, config);
  const cached = getCache.get(key);

  if (!force && cached?.value !== undefined) return cached.value as T;
  if (!force && cached?.promise) return cached.promise as Promise<T>;

  const execute = async (): Promise<T> => {
    let lastError: unknown;

    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const response = await apiClient.get<T>(url, config);
        return response.data;
      } catch (error: unknown) {
        lastError = error;
        const status = typeof error === "object" && error !== null && "status" in error ? Number(error.status) : 0;
        const canRetry = !status || status === 408 || status === 429 || status >= 500;
        if (!canRetry || attempt === 1) throw error;
        await new Promise((resolve) => window.setTimeout(resolve, 350));
      }
    }

    throw lastError;
  };

  const request = execute()
    .then((data) => {
      getCache.set(key, { value: data });
      return data;
    })
    .catch((error) => {
      // Une erreur n'est jamais mémorisée : la prochaine utilisation réessaiera.
      getCache.delete(key);
      throw error;
    });

  getCache.set(key, { promise: request });
  return request;
};

export const clearGetCache = (urlPrefix?: string): void => {
  if (!urlPrefix) {
    getCache.clear();
    return;
  }

  Array.from(getCache.keys()).forEach((key) => {
    if (key.startsWith(urlPrefix)) getCache.delete(key);
  });
};
